import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { AppState, PageSeoMetadata } from '@om-fe/core/core/types';
import Metadata from '@om-fe/core/components/metadata/metadata';
import { EMRoutingContextInterface, RouteNames } from '@om-fe/core/core/routing/utils';
import { EventRouteParams } from '@om-fe/core/core/routing/route-params';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { compose } from 'redux';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import MarketTemplateMappingsContext from '@om-fe/core/components/market-template-mappings-context/marketTemplateMappingsContext';
import { WithMarketTemplateMappingsProps } from '@om-fe/core/components/market-template-mappings-context/with-market-template-mappings';
import { EventStatus, MatchDetailsViewModel, DisciplineLiveStatus, DisciplinesType, sportradarEventStatus } from '@om-fe/models';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import {
    MatchDetailsStateExt,
    SLICE_MATCH_DETAILS,
    subscribeToMatchDetails,
    unsubscribeMatchDetails,
} from '@om-fe/core/components/match-details/match-details.store';
import MatchDetailsHeader from '../components/match-details/match-details-header';
import MatchDetailsSkeleton from '@om-fe/core/components/match-details/match-details-skeleton';
import getSlice from '@om-fe/core/utils/get-slice';
import MarketGroups from '@om-fe/core/components/market-groups/market-groups';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import RedirectTo from '@om-fe/core/components/redirect-to/redirect-to';
import { ACTIVE_MATCH_TYPES } from '@om-fe/core/utils/match';
import { getLiveSportsRouteParams, matchDetailsBem } from '@om-fe/core/components/match-details/utils/match-details.utils';
import { ItemWidthType } from "@om-fe/widgets/dist/components/nav/nav.constants";
import { WidgetType } from '@om-fe/widgets/dist/components/external-sport-widgets/widget.constants';
import { withSelectionBetslipData, WithSelectionBetslipDataModel } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import { MatchDetailsSliderWidgetIds } from '@om-fe/widgets/dist/widgets/match-details-slider/match-details-slider.constants';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { ReactNode } from 'react';
import getConfig, { OMFE2ConfigModel, MatchPageConfig } from '@om-fe/core/utils/config';
import { MatchDetailsHeaderContent } from '../components/match-details/match-details-header-content';
import { EventMappingRpcResponseItem } from '@om-fe/api';
import { SCROLL_CONTAINER_TOP_CLASS } from '@om-fe/core/utils/dom';
import { setVirtualStateFocusVisible } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { isBrowser } from '@om-fe/utils';
import { DEFAULT_EVENT_SLIDER_EVENTS_NUMBER } from '@om-fe/core/components/event-slider/event-slider.constants';
import EventSlider from '@om-fe/core/components/event-slider/event-slider';
import { DeepLink } from '@om-fe/core/utils/deep-link';
import { StmDataModel, stmCustomEvent } from '@om-fe/core/utils/config/sportradar-tag-manager';

interface EventPageStoreProps {
    readonly breakpoints: Breakpoints;
    readonly event?: MatchDetailsViewModel | null;
    readonly lang: string;
    readonly isLarge?: boolean;
}

type EventPageProps = EMRoutingContextInterface
    & EventPageStoreProps
    & WithMarketTemplateMappingsProps
    & RouteComponentProps<EventRouteParams>
    & I18nContextInterface
    & WithSelectionBetslipDataModel;

export interface EventPageState {
    readonly isCountdownActive: boolean;
    readonly activeTab: MatchDetailsSliderWidgetIds | undefined;
    readonly metadata: PageSeoMetadata;
}

const instanceId: string = 'event-page';

class EventPageComponent extends React.PureComponent<EventPageProps, EventPageState> {
    timeoutId: NodeJS.Timeout | undefined = undefined;
    state: EventPageState = {
        isCountdownActive: false,
        activeTab: undefined,
        metadata: {
            title: '',
            keywords: '',
        },
    };

    componentDidMount() {
        const { event, match: { params: { bettingOfferIds, stake } }, addSelection, setMasterStake, setSingleSelectionStake } = this.props;

        if (event) {
            this.updateMetadata(event);
            this.handleScroll();
        }

        DeepLink({ bettingOfferIds, stake, id: event?.eventId, addSelection, setSingleSelectionStake, setMasterStake });
    }

    sendSportradarViewItem() {
        const { event, lang } = this.props;
        const stmView: StmDataModel = {
            event: 'view_item',
            payload: {
                sportsEvent: {
                    match_id: event?.eventId,
                    sr_match_id: '',
                    team_home_name: event?.homeName,
                    team_home_name_short: '',
                    team_home_logo: '',
                    team_away_name: event?.awayName,
                    team_away_name_short: '',
                    team_away_logo: '',
                    scheduled: '',
                    status: event ? sportradarEventStatus?.[event.statusId] ?? '' : '',
                    tournament_name: event?.tournamentName,
                    sport: event?.disciplineName,
                    odds_home: '',
                    odds_draw: '',
                    odds_away: '',
                    language: lang,
                },
            },
        };

        if (stmCustomEvent) {
            stmCustomEvent(stmView);
        }
    }

    componentDidUpdate(prevProps: EventPageProps) {
        const { match: { params: { id } }, lang, event } = this.props;
        const { match: { params: { id: prevId } }, lang: prevLang } = prevProps;

        if (!event || !event?.disciplineId) {
            return <RedirectTo routeName={RouteNames.home} />;
        }

        if (event) {
            if (id !== prevId || lang !== prevLang) {
                this.updateMetadata(event);
            }
            const { isCountdownActive } = this.state;
            const { redirectDelay, statusId } = event;
            const matchIsOver: boolean = !ACTIVE_MATCH_TYPES.includes(statusId as EventStatus);

            if (matchIsOver && redirectDelay && !isCountdownActive) {
                this.showCountdown();
            }
        }
    }

    updateMetadata(event: MatchDetailsViewModel) {
        const { title, awayName, homeName, tournamentName } = event;
        const globalConfig: OMFE2ConfigModel = getConfig();
        const enabledSportradar: boolean = !!globalConfig.sportradar?.clientId;

        this.setState({
            metadata: {
                title,
                keywords: `${homeName}, ${awayName}, ${tournamentName}`,
            },
        });

        if (enabledSportradar) {
            this.sendSportradarViewItem();
        }

    }

    handleScrollCallback = () => {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            setVirtualStateFocusVisible(`.${SCROLL_CONTAINER_TOP_CLASS} article`, '.Market__Legend .Market__CollapseText');
        }, 500);
    };

    componentWillUnmount() {
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            const scrollableElementList: NodeListOf<Element> = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS}`);
            scrollableElementList.forEach((element) => {
                element.removeEventListener('scroll', this.handleScrollCallback);
            });
        }
    }

    handleScroll() {
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            const scrollableElementList: NodeListOf<Element> = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS}`);
            scrollableElementList.forEach((element) => {
                element.addEventListener('scroll', this.handleScrollCallback);
            });
        }
    }

    handleCountdownCallback = (): void => {
        const { history, getPath, event, gettext } = this.props;

        if (!event) {
            return;
        }

        const { disciplineId, disciplineName, sportParentId, sportParentName, displayChildren } = event;

        const liveSportsRouteParams = getLiveSportsRouteParams({
            disciplineId,
            disciplineName,
            sportParentId,
            sportParentName,
            displayChildren,
            allLocationText: gettext('All'),
        });

        history.push(getPath(RouteNames.liveSports, liveSportsRouteParams));
    };

    getRedirectNode(): React.ReactNode {
        const { event, gettext } = this.props;

        if (!event) {
            return <RedirectTo routeName={RouteNames.home} />;
        }

        const { isCountdownActive } = this.state;
        const { disciplineId, disciplineName, sportParentId, sportParentName, displayChildren } = event;
        const { redirectDelay, statusId } = event;
        const matchIsOver: boolean = !ACTIVE_MATCH_TYPES.includes(statusId as EventStatus);

        const liveSportsRouteParams = getLiveSportsRouteParams({
            disciplineId,
            disciplineName,
            sportParentId,
            sportParentName,
            displayChildren,
            allLocationText: gettext('All'),
        });

        if (matchIsOver && !redirectDelay && !isCountdownActive) {
            return <RedirectTo routeName={RouteNames.liveSports} routeParams={liveSportsRouteParams} />;
        } else {
            return null;
        }
    }

    getBreadcrumbsNode(): ReactNode {
        const { match: { params: { id } }, event, isLarge } = this.props;
        const {
            disciplineId,
            locationId,
            parentId: tournamentId,
            venueName,
            categoryName,
            categoryId,
            sportParentName,
            sportParentId,
        } = event;

        if (sportParentId === DisciplinesType.ElectronicSports || sportParentId === DisciplinesType.Fighting) {
            return (
                <Breadcrumbs
                    disciplineId={disciplineId}
                    locationId={locationId}
                    categoryId={categoryId}
                    tournamentId={tournamentId}
                    isLarge={isLarge}
                    eventId={id}
                    routeType={DisciplineRouteType.Location}
                />
            );
        }

        return (
            <Breadcrumbs
                disciplineId={sportParentId || disciplineId}
                locationId={locationId}
                tournamentId={tournamentId}
                sportChildId={sportParentId && disciplineId}
                isLarge={isLarge}
                eventId={id}
                categoryId={categoryId}
                routeType={sportParentName
                    ? DisciplineRouteType.Parent
                    : (categoryName !== venueName ? DisciplineRouteType.Category : DisciplineRouteType.Location)}
            />
        );
    }

    showCountdown(): void {
        this.setState({
            isCountdownActive: true,
        });
    }

    onTabChanged(activeTab: MatchDetailsSliderWidgetIds): void {
        if (activeTab) {
            this.setState({ activeTab });
        }
    }

    render() {
        const { match: { params: { id } }, marketTemplateMappings, event, breakpoints } = this.props;
        const { metadata: { title, keywords }, activeTab } = this.state;

        if (!event || !event?.disciplineId) {
            return <RedirectTo routeName={RouteNames.home} />;
        }

        const {
            disciplineId,
        } = event;

        const redirectNode: React.ReactNode = this.getRedirectNode();
        const globalConfig: OMFE2ConfigModel = getConfig();
        const breakpointSmall: boolean = breakpoints.small;
        const clientId: string | undefined = globalConfig.betradar && globalConfig.betradar.clientId;

        if (redirectNode) {
            return redirectNode;
        }
        const matchPageConfig: MatchPageConfig | undefined = globalConfig.matchPageConfig;
        const scoreWidget: EventMappingRpcResponseItem = { key: WidgetType.BrWidget, value: id };
        const enableEventsSlider: boolean = globalConfig?.enableEventsSlider;
        const showEventSlider: boolean = breakpoints.small || breakpoints.medium;
        const eventSliderEventsNumber: number = (matchPageConfig
            && matchPageConfig.eventSliderConfig
            && matchPageConfig.eventSliderConfig.numberOfEventsSlider)
            || DEFAULT_EVENT_SLIDER_EVENTS_NUMBER;

        return (
            <>
                <Metadata
                    title={title}
                    keywords={keywords}
                    description={title}
                />
                <div className={matchDetailsBem()}>
                    {this.getBreadcrumbsNode()}
                    {breakpointSmall
                        ? <>
                            <MatchDetailsHeader
                                item={event}
                                breakpoints={breakpoints}
                                activeTab={activeTab}
                            />
                            <MatchDetailsHeaderContent
                                scoreWidgetId={scoreWidget}
                                hasBetradarWidget={true}
                                disciplineId={disciplineId}
                                clientId={clientId}
                                matchId={id}
                                event={event}
                                liveStatus={DisciplineLiveStatus.NOT_LIVE}
                                onTabChanged={this.onTabChanged.bind(this)}
                            />
                            {enableEventsSlider && showEventSlider && (
                                <EventSlider
                                    instanceId={instanceId}
                                    disciplineId={disciplineId}
                                    limitNumberOfEvents={eventSliderEventsNumber}
                                    liveStatus={DisciplineLiveStatus.NOT_LIVE}
                                    eventId={id}
                                />
                            )}
                        </>
                        : <MatchDetailsHeader
                            item={event}
                            breakpoints={breakpoints}
                        />
                    }
                    <MarketTemplateMappingsContext.Provider value={marketTemplateMappings}>
                        <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.event, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.MatchDetails }}>
                            <MarketGroups
                                matchId={id}
                                showMarketGroupsHeader={true}
                                itemWidthType={ItemWidthType.ItemToStartUnequalWidth}
                                instanceId={`event-page-${id}`}
                            />
                        </UserBehaviorTrackingContext.Provider>
                    </MarketTemplateMappingsContext.Provider>
                </div>
            </>
        );
    }
}

const EventPage = compose(
    withSelectionBetslipData<EventPageProps>(),
    withTranslation<EventPageProps>(),
    preLoader<EventPageProps>({
        preload({ props, dispatch }) {
            const { match: { params: { id } }, isAuthenForTopic } = props;
            dispatch(subscribeToMatchDetails(id, isAuthenForTopic));
        },
        update({ prevProps, props, dispatch }) {
            const { match: { params: { id } }, isAuthenForTopic } = props;
            const { match: { params: { id: prevId } }, isAuthenForTopic: prevIsAuthenForTopic } = prevProps;

            if (id !== prevId || isAuthenForTopic !== prevIsAuthenForTopic) {
                dispatch(unsubscribeMatchDetails(prevId, prevIsAuthenForTopic));
                dispatch(subscribeToMatchDetails(id, isAuthenForTopic));
            }
        },
        getReadySlice({ }: EventPageProps): string {
            return `${SLICE_MATCH_DETAILS}.ready`;
        },
        destroy: ({ props, dispatch }) => {
            const { match: { params: { id } }, isAuthenForTopic } = props;
            dispatch(unsubscribeMatchDetails(id, isAuthenForTopic));
        },
        loadingAnimation: MatchDetailsSkeleton,
    }),
    connect<EventPageStoreProps, null, EventPageProps, AppState>(
        (state: AppState): EventPageStoreProps => {
            const slice: MatchDetailsStateExt | undefined = getSlice<MatchDetailsStateExt>(state, SLICE_MATCH_DETAILS);

            if (!slice) {
                return {
                    breakpoints: state.breakpoints,
                    lang: state.config.lang,
                };
            }

            return {
                breakpoints: state.breakpoints,
                event: slice.event,
                isLarge: (state.breakpoints.large || state.breakpoints.xl),
                lang: state.config.lang,
            };
        },
    ),
)(EventPageComponent);
export default EventPage;
